import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";

// Load worker from public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const PDFViewer = ({ blobUrl }) => {
    const canvasRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pageNum, setPageNum] = useState(1);
    const [scale, setScale] = useState(1.5);
    const renderTaskRef = useRef(null);

    useEffect(() => {
        const loadPDF = async () => {
            try {
                const loadingTask = pdfjsLib.getDocument(blobUrl);
                const pdf = await loadingTask.promise;
                setPdfDoc(pdf);
                setPageNum(1);
            } catch (error) {
                console.error("PDF load error:", error);
            }
        };

        if (blobUrl) loadPDF();
    }, [blobUrl]);

    useEffect(() => {
        const renderPage = async () => {
            if (!pdfDoc) return;

            try {
                // Cancel any pending render task before starting a new one
                if (renderTaskRef.current) {
                    renderTaskRef.current.cancel();
                    renderTaskRef.current = null;
                }
                
                const page = await pdfDoc.getPage(pageNum);
                const viewport = page.getViewport({ scale });
                
                const canvas = canvasRef.current;
                if (!canvas) return;
                
                const context = canvas.getContext("2d");
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderTask = page.render({ canvasContext: context, viewport });
                renderTaskRef.current = renderTask;
                
                await renderTask.promise;
                renderTaskRef.current = null;
            } catch (error) {
                renderTaskRef.current = null;
                // RenderingCancelledException is expected when we cancel a render task
                if (error.name === "RenderingCancelledException") {
                    return;
                }
                console.error("Page render error:", error);
            }
        };

        renderPage();
    }, [pdfDoc, pageNum, scale]);

    // Cleanup: cancel render task on unmount
    useEffect(() => {
        return () => {
            if (renderTaskRef.current) {
                console.log("PDFViewer: Cleaning up - cancelling render task on unmount");
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }
        };
    }, []);

    const handlePrev = () => setPageNum((prev) => Math.max(prev - 1, 1));
    const handleNext = () => setPageNum((prev) => Math.min(prev + 1, pdfDoc?.numPages || 1));
    const handleZoomIn = () => setScale((prev) => prev + 0.25);
    const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));

    return (
        <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "10px" }}>
                <button onClick={handlePrev} disabled={pageNum <= 1}>⬅ Prev</button>
                <span style={{ margin: "0 10px" }}>
                    Page {pageNum} of {pdfDoc?.numPages || "?"}
                </span>
                <button onClick={handleNext} disabled={pageNum >= (pdfDoc?.numPages || 1)}>Next ➡</button>
                <button onClick={handleZoomOut} style={{ marginLeft: "20px" }}>➖ Zoom Out</button>
                <button onClick={handleZoomIn}>➕ Zoom In</button>
            </div>
            <canvas
                ref={canvasRef}
                style={{
                    border: "1px solid #ccc",
                    backgroundColor: "#fff",
                    maxWidth: "100%",
                    margin: "auto"
                }}
            />
        </div>
    );
};

export default PDFViewer;